import _ from "lodash";

import type { CustomerResponseDto, StatusDto, CustomerAddressRequestDto } from "@src/api-new/generated-api.schemas";

import MEMBER_ROLES from "../constants/MEMBER_ROLES";
import CUSTOMER_STATUSES from "../constants/CUSTOMER_STATUSES";


import { phoneNumberWithOrWithoutZero } from "./customerObjectConvert";

export type CustomerStatusType = StatusDto["status"];

export interface NormalizedCustomerDto {
  id?: number;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  nameWithLeadingLetter?: string;
  additionalField?: string;
  birthDate?: string;
  gender?: string;
  address?: CustomerAddressRequestDto;
  role?: string;
  dni?: string;
  status: CustomerStatusType;
  latestStatus: CustomerStatusType;
  phone?: string;
  phoneWithoutZero?: string;
  email?: string;
  createdBy?: number;
  createdDate?: string;
  isBlacklist?: boolean;
  customerCreditLimit?: number;
  isRtb?: boolean;
  twoLetterName?: string;
  passwordRequired?: boolean;
  newStatusCreatedDate?: string;
}

/**
 * Normalizes customer details DTO to a more usable format.
 * @param {CustomerResponseDto} customerDetails - The customer details to normalize.
 * @returns {Object|undefined} - Normalized customer object or undefined if input is invalid.
 */
export default function normalizeCustomerDto(
  customerDetails: CustomerResponseDto = {}
): NormalizedCustomerDto | undefined {
  try {
    if (!customerDetails || _.isEmpty(customerDetails)) return undefined;

    const {
      id,
      additionalField,
      birthDate,
      customerAddress,
      creditLimit,
      createdBy,
      createdDate,
      firstName,
      gender,
      lastName,
      isRtb,
      isBlacklist,
      passwordRequired,
      statuses,
      communication
    } = customerDetails;

    const latestPhone = _.findLast(_.sortBy(communication, "id"), {
      type: "phone",
      subType: "personal"
    })?.value;

    const latestEmail = _.findLast(_.sortBy(communication, "id"), {
      type: "email",
      subType: "personal"
    })?.value;

    const status = _.last(_.sortBy(statuses, "id"))?.status;

    const customerCreditLimit = _.get(
      _.last(_.sortBy(creditLimit, "id")),
      "limit"
    );

    const twoLetterName = (firstName?.[0] ?? "") + (lastName?.[0] ?? "");

    const address = _.omitBy(
      _.find(customerAddress, {
        addressType: "home"
      }),
      _.isEmpty
    );

    const newStatusCreatedDate = _.get(
      _.find(statuses, {
        status: CUSTOMER_STATUSES.new
      }),
      "createdDate"
    );

    return {
      id,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      nameWithLeadingLetter: `${firstName} ${lastName ? lastName[0] : ""}.`,
      additionalField,
      birthDate,
      gender,
      address,
      role: MEMBER_ROLES.user,
      dni: additionalField,
      status,
      latestStatus: status,
      phone: latestPhone,
      phoneWithoutZero: phoneNumberWithOrWithoutZero(latestPhone),
      email: latestEmail,
      createdBy,
      createdDate,
      isBlacklist,
      customerCreditLimit,
      isRtb,
      twoLetterName,
      passwordRequired,
      newStatusCreatedDate
    };
  }
  catch (err) {
    console.error("normalizeCustomerDto error:", err);
    return undefined;
  }
}
